import { FC, useId, useState } from "react";
import { Collapse } from "@mantine/core";
import { useTranslation } from "react-i18next";
import Checkbox from "@/components/atoms/Checkbox";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import TextInput from "@/components/atoms/TextInput";
import styles from "./styles.module.css";

/** Collapsed summary shows this many selected services before falling back to "+N more". */
const MAX_SUMMARY_CHIPS = 3;
/** Below this many services a search box is just noise. */
const SEARCH_MIN_SERVICES = 6;

export interface CompanyServiceItem {
  id: number;
  name: string;
  price: number;
  currency: string;
  /** Payment interval code, translated via `enumerables:paymentIntervals.<code>`. */
  priceUnitCode: string;
  checked: boolean;
}

export interface CompanyServicesAccordionProps {
  services: CompanyServiceItem[];
  /** Disables the checkboxes and hides the "Change" price action. */
  disabled?: boolean;
  /**
   * Only list the selected services, and don't reveal how many others exist. For viewers who don't own the
   * services: the owner's catalog can hold customer-specific services that other parties shouldn't see.
   */
  hideUnselected?: boolean;
  onToggle: (id: number, checked: boolean) => void;
  onPriceChange: (id: number, price: number) => void;
}

/**
 * Collapsible list of a company's own services (checkbox + agreed price). Collapsed by default, since companies
 * can have dozens; the selected ones stay visible as a summary. Purely presentational: the caller owns the
 * selection and price state.
 */
function CompanyServicesAccordion({
  services: allServices,
  disabled,
  hideUnselected,
  onToggle,
  onPriceChange,
}: CompanyServicesAccordionProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<{ id: number; draft: string } | null>(null);

  const services = hideUnselected ? allServices.filter((service) => service.checked) : allServices;
  if (services.length === 0) return null;

  const unitLabel = (code: string) => t("enumerables:paymentIntervals." + code).toLowerCase();
  const selected = services.filter((service) => service.checked);
  const normalizedQuery = query.trim().toLowerCase();
  const visibleServices = normalizedQuery
    ? services.filter((service) => service.name.toLowerCase().includes(normalizedQuery))
    : services;

  // The draft string lets "1." and "1,5" be typed; only the parsed number is reported upwards.
  const handleDraftChange = (id: number, draft: string) => {
    setEditing({ id, draft });
    const parsed = parseFloat(draft.replace(",", "."));
    onPriceChange(id, Number.isNaN(parsed) ? 0 : parsed);
  };

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.header}
        data-testid="company-services-toggle"
        data-expanded={isExpanded}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <span className={styles.headerTitle}>
          <Text size="md" strong={true}>
            {t("contracts:extraServices")}
          </Text>
          <span className={styles.count}>
            {hideUnselected
              ? t("contracts:servicesSelectedOnly", { count: selected.length })
              : t("contracts:servicesSelectedCount", { selected: selected.length, total: services.length })}
          </span>
        </span>
        <Text size="sm" color="tertiary">
          {isExpanded ? t("actions:showLess") : t("actions:showMore")}
        </Text>
        <SVGAsset name="chevron-down-grey" className={styles.chevron} width="20" height="20" alt="" />
      </button>

      {!isExpanded && (
        <div className={styles.summary} data-testid="company-services-summary">
          {selected.length === 0 ? (
            <Text size="sm" color="tertiary">
              {t("contracts:noServicesSelected")}
            </Text>
          ) : (
            <>
              {selected.slice(0, MAX_SUMMARY_CHIPS).map((service) => (
                <span key={service.id} className={styles.chip}>
                  <Text size="sm">{service.name}</Text>
                  <Text size="xs" color="tertiary">
                    <PriceLabel service={service} unit={unitLabel(service.priceUnitCode)} />
                  </Text>
                </span>
              ))}
              {selected.length > MAX_SUMMARY_CHIPS && (
                <Text size="sm" color="tertiary">
                  {t("contracts:moreServices", { count: selected.length - MAX_SUMMARY_CHIPS })}
                </Text>
              )}
            </>
          )}
        </div>
      )}

      <Collapse expanded={isExpanded} id={panelId}>
        <div className={styles.panel}>
          {services.length > SEARCH_MIN_SERVICES && (
            <TextInput
              value={query}
              placeholder={t("contracts:searchServices")}
              aria-label={t("contracts:searchServices")}
              rightSection={<SVGAsset name="search" alt="" />}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}

          {visibleServices.length === 0 ? (
            <Text size="sm" color="tertiary">
              {t("contracts:noServicesMatch", { query: query.trim() })}
            </Text>
          ) : (
            <ul className={styles.list}>
              {visibleServices.map((service) => {
                const isEditing = editing?.id === service.id;
                const checkboxId = `${panelId}-service-${service.id}`;
                return (
                  <li key={service.id} className={`${styles.row} ${service.checked ? styles.rowSelected : ""}`}>
                    {/* The checkbox is centred against the whole two-line row, so the name sits in its own label. */}
                    <Checkbox
                      id={checkboxId}
                      className={styles.rowCheckbox}
                      data-testid={`companySpecificService-checkbox-${service.name}`}
                      disabled={disabled}
                      checked={service.checked}
                      onChange={(e) => onToggle(service.id, e.currentTarget.checked)}
                    />
                    <label
                      htmlFor={checkboxId}
                      className={styles.name}
                      data-disabled={disabled || undefined}
                      data-testid={`companySpecificService-${service.name}`}
                    >
                      {service.name}
                    </label>
                    <div className={styles.price}>
                      {isEditing ? (
                        <>
                          <TextInput
                            size="xs"
                            autoFocus
                            inputMode="decimal"
                            className={styles.priceInput}
                            data-testid={`companySpecificServicePrice-${service.name}`}
                            aria-label={service.name}
                            value={editing.draft}
                            onChange={(e) => handleDraftChange(service.id, e.target.value)}
                            onBlur={() => setEditing(null)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                // Inside a wizard <form>, Enter would otherwise submit the step.
                                e.preventDefault();
                                setEditing(null);
                              }
                            }}
                          />
                          <Text size="xs" color="tertiary">
                            {service.currency}/{unitLabel(service.priceUnitCode)}
                          </Text>
                        </>
                      ) : (
                        <>
                          <Text size="sm" color="tertiary">
                            <PriceLabel service={service} unit={unitLabel(service.priceUnitCode)} />
                          </Text>
                        </>
                      )}
                    </div>
                    {!disabled && !isEditing && (
                      <button
                        type="button"
                        className={styles.changeButton}
                        data-testid={`companySpecificService-edit-${service.name}`}
                        onClick={() => setEditing({ id: service.id, draft: String(service.price) })}
                      >
                        {t("actions:change")}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Collapse>
    </div>
  );
}

export default CompanyServicesAccordion;

const PriceLabel: FC<{ service: CompanyServiceItem; unit: string }> = ({ service, unit }) => (
  <NumberFormatter value={service.price} suffix={` ${service.currency}/${unit}`} />
);
